import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import TableComponent from '../../common/components/TableComponent/TableComponent';
import AddApplicationProfile from './AddApplicationProfile';
import { deleteApplicationProfile, getApplicationProfiles, getBusinessUnits, getCompositeApplicationProfile, getTestTypes } from '../../redux/actions/ApplicationProfile/applicationProfile';
import Actions from 'common/components/Actions/Actions';
import { MotifIcon } from '@ey-xd/motif-react';
import { actionIcDelete24px} from "@ey-xd/motif-react/assets/icons";
import DeleteModal from 'common/components/Modal/DeleteModal';
import Toast from 'common/components/Toast/Toast';
import { MotifProgressLoader } from '@ey-xd/motif-react';

function ApplicationProfile() {
  const dispatch = useDispatch();
  const location = useLocation();
  const data = useSelector((state: any) => state?.applicationProfile?.data?.data) || [];
  const loading = useSelector((state: any) => state?.applicationProfile?.loading) || false;
  const currentTenantId = useSelector((state: any) => state?.user?.currentTenant?.TenantId);
  const currentTenant = useSelector((state: any) => state?.user?.currentTenant);
  const isAiaTenant = (currentTenant?.TenantName || '').toLowerCase() === 'aia';

  const columns = useMemo(() => [
    { 
      field: 'ApplicationName', 
      headerName: 'Application Name', 
      headerComponentParams: {
        enableSorting: true,
        showFilter: false
      },
      sortable: true,
      width: 300 
    },
    { field: 'TestTypeName', headerName: 'Test Type', sortable: true, width: 300 },
    { field: 'TestingEnvironment', headerName: 'Testing Environment', sortable: true, width: 200 },
    { field: 'BusinessUnitName', headerName: 'Business Unit', sortable: true, width: 200 },
    ...(isAiaTenant ? [{ field: 'WorkOrder', headerName: 'Work Order Number', sortable: true, width: 200 }] : []),
    { field: 'UserRoles', headerName: 'User Roles', sortable: true, width: 200 },
    { field: 'Description', headerName: 'Description', sortable: true, width: 350 },
    {
      cellRenderer: 'actionsCellRenderer',
      field: 'actions',
      pinned: 'right',
      headerComponentParams: {
        enableSorting: false,
        showFilter: false
      },
      headerName: 'Actions',
      width: 250
    }
  ], [isAiaTenant]);

  const searchKeys = useMemo(() => {
    const base = ['ApplicationName', 'TestTypeName', 'BusinessUnitName', 'UserRoles', 'Description'];
    return isAiaTenant ? ['ApplicationName', 'TestTypeName', 'BusinessUnitName', 'WorkOrder', 'UserRoles', 'Description'] : base;
  }, [isAiaTenant]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<any | null>(null);
  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [actionItemId, setActionItemId] = useState<string>('');
  const [actionItemName, setActionItemName] = useState<string>('');
  const [toastVisible, setToastVisible] = useState(false)
  const [toastData, setToastData] = useState<any>()

  useEffect(() => {
    dispatch(getApplicationProfiles());
  }, []);

  useEffect(() => {
    if (location.state?.showForm) {
      handleAddNewApplication();
    }
  }, [location.state?.showForm]);

  useEffect(() => {
    if (currentTenantId) {
      dispatch(getApplicationProfiles());
    }
  }, [currentTenantId]);

  const handleAddNewApplication = () => {
    dispatch(getTestTypes());
    dispatch(getBusinessUnits());
    setEditItem(null);
    setShowForm(true);
  };

  const renderContent = () => (
    <TableComponent
      breadCrumbs={<>Applications Profile</>}
      title="Applications Profile"
      description="Manage applications across the application."
      columns={columns}
      columnActions={{
        actionsCellRenderer: (p: any) => (
          <Actions
            items={[{
              label: 'Details',
              onClick: () => actionHandler(p?.data?.ApplicationId, p?.data?.ApplicationName, 'edit'),
              variant: 'secondary',
              size: 'medium'
            },
            {
              label: <MotifIcon src={actionIcDelete24px} style={{ width: 20 }} />,
              onClick: () => actionHandler(p?.data?.ApplicationId, p?.data?.ApplicationName, 'delete'),
              variant: 'secondary',
              size: 'small'
            }]}
          />
        )
      }}
      data={data}
      loading={loading}
      searchPlaceholder="Search"
      searchKeys={searchKeys}
      tabCount={data.length}
      actionButtonLabel="+ Add New Application"
      onActionButtonClick={handleAddNewApplication}
      emptyStateMessage='No applications found'
    />
  );
  
  const actionHandler = (id: string, name: string, actionType: string) => {
    dispatch(getTestTypes());
    dispatch(getBusinessUnits());
    if (actionType === 'edit') {
      // Fetch composite profile, map to form data, then open form
      dispatch(getCompositeApplicationProfile(id))
        .then((result: any) => {
          const app = result?.application || result?.Application || {};
          // Map API response fields to form arrays with correct key names
          const urls = (result?.urls || []).map((x: any) => ({
            value: x?.URL ?? x?.Url ?? x?.url ?? '',
            description: x?.UrlDescription ?? x?.URLDescription ?? x?.description ?? '',
          }));
          const endpointsSrc = result?.endpoints || result?.Endpoints || app?.endpoints || app?.Endpoints || app?.ApplicationEndpoints || [];
          const endpoints = (endpointsSrc as any[]).map((x: any) => ({
            value:
              x?.Endpoint ?? x?.EndPoint ?? x?.APIEndpoint ?? x?.ApiEndpoint ??
              x?.Path ?? x?.Url ?? x?.URL ?? x?.endpoint ?? '',
            description:
              x?.EndpointDescription ?? x?.endpointDescription ?? x?.Description ??
              x?.UrlDescription ?? x?.URLDescription ?? x?.description ?? '',
            httpMethod: x?.HTTPMethod ?? x?.Method ?? x?.HttpMethod ?? x?.httpMethod ?? x?.Verb ?? x?.Type ?? x?.RequestMethod ?? x?.RequestType ?? x?.MethodType ?? x?.method ?? undefined,
          }));
          const ips = (result?.ips || []).map((x: any) => ({
            value: x?.Ip ?? x?.ip ?? '',
            description: x?.IpDescription ?? x?.description ?? '',
          }));
          const servers = (result?.servers || []).map((x: any) => ({
            value: x?.Server ?? x?.server ?? '',
            description: x?.ServerDescription ?? x?.description ?? '',
          }));
          const binaryChecksums = (result?.binary_checksums || []).map((x: any) => ({
            value: x?.BinaryChecksum ?? x?.checksum ?? '',
            description: x?.BinaryChecksumDescription ?? x?.description ?? '',
          }));

          const editData: any = {
            ApplicationId: app.ApplicationId || id,
            testType: app.TestTypeName || result?.TestTypeName || '',
            applicationName: app.ApplicationName || '',
            businessUnit: app.BusinessUnitName || result?.BusinessUnitName || '',
            testingEnvironment: app.TestingEnvironment || result?.TestingEnvironment || '',
            workOrder: app.WorkOrder || result?.WorkOrder || '',
            urls,
            endpoints,
            ips,
            servers,
            binaryChecksums,
            userRoles: app.UserRoles || '',
            description: app.Description || '',
            ApplicationVersion: app.ApplicationVersion || result?.ApplicationVersion || '',
          };
          setEditItem(editData);
          setShowForm(true);
        })
        .catch(() => {
          setToastVisible(true);
          setToastData({
            header: 'Failed',
            message: 'Failed to load application profile for editing.',
            variant: 'error'
          });
        });
      return;
    }

    if (actionType === 'delete') {
      setIsDeleteVisible(true);
    }
    setActionItemName(name);
    setActionItemId(id);
  }

  const handleDeleteReport = () => {
      dispatch(deleteApplicationProfile(actionItemId) as any).then((res: any) => {   
        setIsDeleteVisible(false);
        dispatch(getApplicationProfiles(true) as any);
        setToastVisible(true)
        setToastData({
          header: "Successfully deleted",
          message: "Your have successfully deleted an application profile. It won't be available in Applications Profile any more.",
          variant: "success"
        })
      }).catch((error: any) => {
        setIsDeleteVisible(false);
        setToastVisible(true)
        setToastData({
          header: "Failed",
          message: error?.data?.message || "Failed to delete application profile. Please try again.",
          variant: "error"
        })
      })
    }

  return (
    <div>
        <MotifProgressLoader show={loading} className="fullscreen-progress-loader" />
        {showForm ? (
          <AddApplicationProfile
            initialData={editItem || undefined}
            mode={editItem ? 'edit' : 'add'}
            onCancel={() => setShowForm(false)}
            onSubmit={(message, status) => {
              setShowForm(false);
              setToastVisible(true);
              const m = String(message || '');
              const isError = status === 'error';
              setToastData({
                header: isError ? 'Failed' : 'Successfully saved',
                message: m || (isError ? 'Operation failed.' : 'Operation succeeded.'),
                variant: status,
              });
            }}
          />
        ) : (
          renderContent()
        )}
        <DeleteModal
          isVisible={isDeleteVisible}
          setIsVisible={setIsDeleteVisible}
          message="Are you sure you want to delete this application profile? This action cannot be undone."
          columnData={ actionItemName }
          onConfirmClick={handleDeleteReport}
        />
        <Toast
            visible={toastVisible}
            setVisible={setToastVisible}
            children={
              <div>
                <div className={'toastHeader'}>{toastData?.header}</div>
                <div className={'toastContent'}>{toastData?.message}</div>
              </div>
            }
            variant={toastData?.variant}
        />
    </div>
  );
}

export default ApplicationProfile;